import React, { useState } from 'react';
import { Card, Typography, Button, Space } from 'antd';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import type { CardProps } from 'antd/lib/card';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  cardStyle: {
    width: '100%',
    marginBottom: '10px',
  },
  iconStyle: {
    fontSize: '14px',
    marginLeft: '5px',
  },
  heading: {
    display: 'inline-block',
    fontSize: '18px !important',
    marginBottom: '5px !important',
  },
  subHeading: {
    display: 'block',
    marginRight: '90px',
    whiteSpace: 'normal',
    fontSize: '12px',
    marginTop: -5,
  },
  headingCount: {
    fontSize: '16px',
    color: '#687078 !important',
    marginLeft: '5px',
  },
});

const { Text, Title } = Typography;

export interface SimulationCardProps extends CardProps {
  heading: string;
  headingOnClick?: () => void;
  headingIcon?: React.ReactNode;
  extraHeading?: React.ReactNode;
  subHeading?: string | React.ReactNode;
  headingCount?: number;
  collapsable?: boolean;
  collapsed?: boolean;
  archived?: boolean;
  addAction?: () => void;
  editAction?: () => void;
  deleteAction?: () => void;
  exportAction?: () => void;
  detailAction?: () => void;
  extraAction?: React.ReactNode;
}

export const SimulationCard = ({
  children,
  heading,
  headingOnClick,
  headingIcon,
  subHeading,
  extraHeading,
  headingCount,
  collapsable,
  collapsed,
  addAction,
  editAction,
  deleteAction,
  exportAction,
  detailAction,
  archived,
  extraAction,
  ...rest
}: SimulationCardProps) => {
  const classes = useStyles();
  const [isCollapsed, setCollapsed] = useState(collapsed);
  const headingCountEl = headingCount ? <span className={classes.headingCount}>({headingCount})</span> : undefined;
  const icon = isCollapsed ? (
    <CaretRightOutlined className={classes.iconStyle} />
  ) : (
    <CaretDownOutlined className={classes.iconStyle} />
  );
  const title = collapsable ? (
    <Title className={classes.heading} level={5} onClick={() => setCollapsed(!isCollapsed)}>
      {headingIcon}
      {heading}
      {headingCountEl}
      {icon}
    </Title>
  ) : (
    <Title onClick={headingOnClick} className={classes.heading} level={5}>
      {headingIcon}
      {heading}
      {headingCountEl}
    </Title>
  );
  const titleDescription = subHeading ? (
    <div>
      {title} {!!extraHeading && extraHeading}{' '}
      <Text className={classes.subHeading} type={'secondary'}>
        {subHeading}
      </Text>
    </div>
  ) : (
    title
  );
  const bodyStyle = collapsable === true && isCollapsed === true ? { padding: 0 } : rest.bodyStyle;
  const actions: React.ReactNode[] = [];

  if (extraAction) {
    actions.push(extraAction);
  }

  if (typeof editAction === 'function' && !archived) {
    actions.push(
      <Button size={'small'} onClick={() => editAction()}>
        Edit
      </Button>,
    );
  }
  if (typeof deleteAction === 'function' && !archived) {
    actions.push(
      <Button size={'small'} onClick={() => deleteAction()}>
        Delete
      </Button>,
    );
  }
  if (typeof exportAction === 'function' && !archived) {
    actions.push(
      <Button size={'small'} onClick={() => exportAction()}>
        Export
      </Button>,
    );
  }
  if (typeof addAction === 'function' && !archived) {
    actions.push(
      <Button size={'small'} onClick={() => addAction()}>
        Add
      </Button>,
    );
  }
  if (typeof detailAction === 'function' && !archived) {
    actions.push(
      <Button size={'small'} onClick={() => detailAction()}>
        View Details
      </Button>,
    );
  }

  return (
    <Card
      title={titleDescription}
      type={'inner'}
      size="small"
      className={classes.cardStyle}
      extra={<Space>{actions}</Space>}
      bodyStyle={bodyStyle}
      {...rest}
    >
      <div hidden={collapsable === true && isCollapsed === true}>{children}</div>
    </Card>
  );
};
